import type { MaintenanceNotice, MaintenanceNoticeDisplay } from './maintenance.models';

function parseInstant(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatUtcTime(date: Date): string {
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    hour12: false,
  }).format(date);
  return `${time} UTC`;
}

function formatLocalInstant(date: Date): string {
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(date);
  const monthDay = new Intl.DateTimeFormat('en-GB', { month: 'short', day: 'numeric' }).format(
    date,
  );
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${weekday}, ${monthDay} · ${time}`;
}

function formatScheduleInstant(date: Date): string {
  return `${formatLocalInstant(date)} (${formatUtcTime(date)})`;
}

function formatRelativeLeadTime(startAt: Date, now: Date): string {
  const remainingMs = startAt.getTime() - now.getTime();
  if (remainingMs <= 0) {
    return '';
  }

  const dayMs = 24 * 60 * 60 * 1000;
  const hourMs = 60 * 60 * 1000;
  const days = Math.floor(remainingMs / dayMs);
  const hours = Math.floor((remainingMs % dayMs) / hourMs);

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) {
    const minutes = Math.max(1, Math.ceil(remainingMs / (60 * 1000)));
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
}

function formatDuration(startAt: Date, endAt: Date): string {
  const durationMs = endAt.getTime() - startAt.getTime();
  if (durationMs <= 0) {
    return '';
  }

  const hours = Math.round(durationMs / (60 * 60 * 1000));
  if (hours >= 1) {
    return `~${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const minutes = Math.max(1, Math.round(durationMs / (60 * 1000)));
  return `~${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * User-facing maintenance copy for the compact banner layout.
 */
export function formatMaintenanceNotice(
  notice: MaintenanceNotice,
  now: Date = new Date(),
): MaintenanceNoticeDisplay {
  const startAt = parseInstant(notice.startAt);
  const endAt = parseInstant(notice.endAt);
  if (!startAt || !endAt || endAt.getTime() <= startAt.getTime()) {
    return {
      headline: 'Planned maintenance downtime',
      details: ['Scheduled maintenance window details are not available.'],
    };
  }

  const duration = formatDuration(startAt, endAt);

  if (now.getTime() >= startAt.getTime()) {
    return {
      headline: 'Planned maintenance downtime in progress',
      details: [`Until ${formatScheduleInstant(endAt)}`],
    };
  }

  const leadTime = formatRelativeLeadTime(startAt, now);
  const details = [formatScheduleInstant(startAt)];
  if (duration) {
    details.push(`Duration: ${duration}`);
  }

  return {
    headline: `Planned maintenance downtime in ${leadTime}`,
    details,
  };
}
